import { useCallback } from "react";

import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  parseErrorResponse,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";
import { selectWireImportBatchesTotal } from "./selectors";

export const useFetchMappings = () => {
  const { send, loading, status, data } = useRequestWithFeedback<{
    results: API.WireImport.CSVFieldMapping[];
  }>();

  const fetchMappings = useCallback(() => {
    return send({
      action: API.fetchMappings(),
    });
  }, [send]);

  return useObjectMemo({
    error: status === "error",
    send: fetchMappings,
    loading,
    data,
  });
};

export const useCreateMapping = () => {
  const { send, loading } =
    useRequestWithFeedback<API.WireImport.CSVFieldMapping>();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const createMapping = useCallback(
    (payload: API.WireImport.CSVFieldMappingRequest) => {
      return send({
        action: API.createMapping(payload),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    createMapping,
    loading,
  });
};

export const useUpdateMapping = () => {
  const { send, loading } =
    useRequestWithFeedback<API.WireImport.CSVFieldMapping>();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const updateMapping = useCallback(
    (uuid: string, payload: API.WireImport.CSVFieldMappingRequest) => {
      return send({
        action: API.updateMapping(uuid, payload),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    updateMapping,
    loading,
  });
};

export const useDeleteMapping = () => {
  const { send, loading } = useRequestWithFeedback();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const deleteMapping = useCallback(
    (uuid: string) => {
      return send({
        action: API.deleteMapping(uuid),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    deleteMapping,
    loading,
  });
};

export const useParseCSV = () => {
  const { send, loading, status } =
    useRequestWithFeedback<API.WireImport.ParseCSV.Response>();

  const parseCSV = useCallback(
    (payload: API.WireImport.ParseCSV.Request) => {
      return send({
        action: API.parseCSV(payload),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    parseCSV,
    loading,
  });
};

export const useSubmitWireImport = () => {
  const { send, loading, status } =
    useRequestWithFeedback<API.WireImport.Submit.Response>();
  const dispatch = useAppDispatch();
  const currentTotal = useSelector(selectWireImportBatchesTotal);

  const submit = useCallback(
    (payload: API.WireImport.Submit.Request) => {
      return send({
        action: API.submit(payload),
        onData: (batch) => {
          dispatch(
            actions.appendResults({
              results: [batch],
              total: currentTotal + 1,
            }),
          );
        },
      });
    },
    [send, dispatch, currentTotal],
  );

  return useObjectMemo({
    error: status === "error",
    submit,
    loading,
  });
};

export const useFetchBatch = () => {
  const { send, loading, status } =
    useRequestWithFeedback<API.WireImport.WireImportBatch>();
  const dispatch = useAppDispatch();

  const fetchBatch = useCallback(
    (uuid: string) => {
      return send({
        action: API.fetchBatch(uuid),
        onData: (batch) => {
          dispatch(actions.updateSelected(batch));
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchBatch,
    loading,
  });
};
